import React, { useEffect } from "react";
import CroboContainer from "../../components/croboContainer";
import { Box, Button, Card, Container, Grid, Typography, useTheme } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import TransferSvg from "../../assets/transfer_pending.svg";
import { useSelector } from "react-redux";
import {
  amountRoundOff,
  analytics,
  convertToDateWithTime,
  triggerHaptic,
} from "../../helper/helper";
import { SupportButton } from "../support";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { isWeb } from "../../utils/platform";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import {
  fadeInAnimation,
  slideUpAnimation,
  pulseAnimation,
  buttonTransition,
} from "../../styles/animations";

const TransactionComplete = () => {
  const user = useSelector((state) => state.userAuth);
  const transaction = useSelector((state) => state.transaction);
  const navigate = useNavigate();
  const { fetchUser } = user;
  const theme = useTheme(); 

  useEffect(() => {
    if (!transaction.sendPayment) {
      navigate("/transaction");
    }
  }, [transaction.sendPayment, navigate]);

  useEffect(() => {
    const handleTransactionComplete = async () => {
      if (!transaction.sendPayment?.message) {
        await triggerHaptic("heavy");
      }
    };
    handleTransactionComplete();
  }, [transaction.sendPayment?.message]);

  const parseMessage = (error) => {
    analytics?.track("Transaction Failed Reason", { error });
    let message = "";
    let code = "";
    switch (error) {
      case "Bank details not verified.":
        code = "RECIPIENT_BANK_DETAILS_INVALID";
         message = "Oops! We couldn't verify the recipient's bank details. Please double-check the account number and IFSC code, or ensure it's not an NRE account.";
         break;
      case "Financial institution did not respond":
        code = "SOURCE_BANK_NOT_RESPONDING";
        message = "Sorry, your bank is currently unresponsive. Please try again in a few minutes. If the issue continues, contact your bank or reach out to us at care@crobo.money.";
        break;
      case "Insufficient funds":
        code = "INSUFFICIENT_FUNDS";
        message =
          "There was a problem completing the transaction due to low balance. Please ensure you have sufficient funds and try again.";
          break;
      case "Transaction failed due to low balance in the source bank.":
        code = "LOW_BALANCE";
        message =
          "There was a problem completing the transaction due to low balance. Please ensure you have sufficient funds and try again.";
          break;
      case "Customer has not been verified":
        code = "RECIPIENT_NOT_VERIFIED";
        message = "The transaction failed due to unexpected error. Please try again after some time. If the problem persists contact our support team.";
        break;
      default:
        if (error.trim().split(/\s+/).length > 4) {
          message = error;
        }
        code = "OTHER";
        message = "Please try again after sometime. If the problem persists then write to us at care@crobo.money.";
    }
    analytics?.track("Transaction Failed Reason", { error, message, code });
    return message;
  };

  // Added Contact Support Button
  return (
    <CroboContainer>
      <Container maxWidth="sm">
        <Card
          sx={{
            borderRadius: 3,
            my: 3,
            overflow: "hidden",
            ...fadeInAnimation,
          }}
        >
         
          {/* Image and Content */}
          <Box sx={{ p: 3 }}>

            <Typography
              variant="h5"
              color={transaction.sendPayment?.message
                ? "error.light"
                : "brand.main"}
                fontWeight={600}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pt: 1,
              }}
            >
              {transaction.sendPayment?.message ? (
                <>
                  <ErrorOutlineIcon />
                  Transaction failed
                </>
              ) : (
                <>
                  <CheckCircleOutlineIcon />
                  Transfer successful
                </>
              )}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mb: 3,
                ...slideUpAnimation,
              }}
            >
              <img
                src={TransferSvg}
                alt="transfer status"
                style={{
                  height: "180px",
                  maxWidth: "100%",
                }}
              />
            </Box>

            {transaction.sendPayment && transaction?.sendPayment?.message ? (
              <Typography
                textAlign="center"
                color="black"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  fontSize: "1rem",
                  lineHeight: 1.5,
                }}
              >
                {parseMessage(transaction?.sendPayment?.message)}
              </Typography>
            ) : (
              <Box>
                {/* Recipient Info */}
                <Box
                  sx={{
                    mb: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    Money sent to
                  </Typography>
                  <Typography variant="h6" fontWeight={600}>
                    {fetchUser.name}
                  </Typography>
                </Box>

                {/* Amount Details */}
                <Box
                  sx={{
                    bgcolor: "grey.100",
                    borderRadius: 2,
                    p: 2,
                    mb: 3,
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography
                        color={theme.palette.mode === "dark" ? "text.invert" : "text.primary"}
                        variant="body2"
                        gutterBottom
                      >
                        You sent
                      </Typography>
                      <Typography variant="h5" fontWeight={600} color={theme.palette.mode === "dark" ? "text.invert" : "text.primary"}>
                        $
                        {amountRoundOff(
                          transaction?.sendPayment?.base_sub_total
                        )}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        color={theme.palette.mode === "dark" ? "text.invert" : "text.primary"}
                        variant="body2"
                        gutterBottom
                      >
                        They receive
                      </Typography>
                      <Typography
                        variant="h5"
                        fontWeight={600}
                        color="brand.main"
                      >
                        ₹
                        {amountRoundOff(
                          transaction?.sendPayment?.base_total_amount
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Transfer Time */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                    color: "text.secondary",
                  }}
                >
                  <AccessTimeIcon fontSize="small" />
                  <Typography variant="body2">
                    {convertToDateWithTime(
                      transaction?.sendPayment?.transactional_time
                    )}{" "}
                    UTC
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Card>

        {/* Action Buttons */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            ...slideUpAnimation,
          }}
        >
          <Link to="/transaction/history" style={{ textDecoration: "none" }}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              sx={{
                py: 1.5,
                bgcolor: "brand.main",
                "&:hover": {
                  bgcolor: "brand.dark",
                },
                ...buttonTransition,
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color:"text.invert",
                }}
              >
                View transaction history
              </Typography>
            </Button>
          </Link>

          <SupportButton
            variant="outlined"
            fullWidth
            size="large"
            sx={{
              py: 1.5,
              borderColor: "brand.main",
              color: "brand.main",
              "&:hover": {
                borderColor: "brand.dark",
                bgcolor: "transparent",
              },
              ...buttonTransition,
            }}
          />
        </Box>
      </Container>
    </CroboContainer>
  );
};

export default TransactionComplete;
